/**
 * RandomState - Seeded pseudorandom number generator for deterministic rendering
 * 
 * This class provides a seeded random number generator that produces consistent,
 * reproducible sequences of random numbers. This ensures that preview and print
 * canvases produce identical results when given the same seed.
 * 
 * ALGORITHM: Linear Congruential Generator (LCG)
 * - Uses well-tested constants from Numerical Recipes
 * - Period: ~4.3 billion numbers before repetition
 * - Statistical quality: Lower than Math.random() but sufficient for UI purposes
 * 
 * PRODUCTION SUITABILITY:
 * ✓ Excellent for this use case - deterministic behavior is essential
 * ✓ Adequate statistical quality for color selection, shuffling, layouts
 * ✗ Not suitable for cryptographic purposes or high-precision simulations
 * 
 * The trade-off of lower randomness quality for deterministic behavior is 
 * intentional and appropriate for ensuring preview/print consistency.
 */
export class RandomState {
    
    constructor(seed = null) {
        // Use current timestamp as default seed if none provided
        this.seed(seed || Date.now());
        this.originalSeed = this.currentSeed;
    }
    
    /**
     * Sets the seed for the random number generator
     * Uses a simple Linear Congruential Generator (LCG) algorithm
     * 
     * @param {number} seedValue - The seed value (will be converted to positive 32-bit integer)
     */
    seed(seedValue) {
        this.currentSeed = Math.abs(Math.floor(seedValue)) % 2147483647; // Keep within 32-bit signed int range
        if (this.currentSeed <= 0) {
            this.currentSeed += 2147483646;
        }
        this.originalSeed = this.currentSeed;
    }
    
    /**
     * Generates the next random number in the sequence
     * Uses LCG algorithm: (a * seed + c) % m
     * 
     * @returns {number} A floating point number between 0 (inclusive) and 1 (exclusive)
     */
    random() {
        // LCG constants from Numerical Recipes
        this.currentSeed = (this.currentSeed * 1664525 + 1013904223) % 4294967296;
        return this.currentSeed / 4294967296;
    }
    
    /**
     * Generates a random integer between min (inclusive) and max (exclusive)
     * 
     * @param {number} min - Minimum value (inclusive)
     * @param {number} max - Maximum value (exclusive)
     * @returns {number} Random integer in range [min, max)
     */
    randomInt(min, max) {
        return Math.floor(this.random() * (max - min)) + min;
    }
    
    /**
     * Generates a random floating point number between min and max
     * Compatible with the existing imgUtil.linearRandom function
     * 
     * @param {number} min - Minimum value
     * @param {number} max - Maximum value  
     * @returns {number} Random float in range [min, max]
     */
    linearRandom(min, max) {
        return min + this.random() * (max - min);
    }
    
    /**
     * Randomly chooses one of the provided values
     * 
     * @param {Array} choices - Array of values to choose from
     * @returns {*} One of the values from the choices array
     */
    choice(choices) {
        if (!choices || choices.length === 0) {
            return null;
        }
        return choices[this.randomInt(0, choices.length)];
    }
    
    /**
     * Randomly shuffles an array in place using Fisher-Yates algorithm
     * 
     * @param {Array} array - Array to shuffle
     * @returns {Array} The same array, shuffled in place
     */
    shuffle(array) {
        const arr = [...array]; // Create a copy to avoid mutating original
        for (let i = arr.length - 1; i > 0; i--) {
            const j = this.randomInt(0, i + 1);
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
    }
    
    /**
     * Generates a random boolean value
     * 
     * @param {number} probability - Probability of returning true (0.0 to 1.0)
     * @returns {boolean} Random boolean
     */
    randomBool(probability = 0.5) {
        return this.random() < probability;
    }
    
    /**
     * Gets the current internal state of the generator
     * 
     * @returns {Object} Object containing current seed and original seed
     */
    getState() {
        return {
            currentSeed: this.currentSeed,
            originalSeed: this.originalSeed
        };
    }
    
    /**
     * Restores the generator to a previous state
     * 
     * @param {Object} state - State object returned by getState()
     */
    setState(state) {
        this.currentSeed = state.currentSeed;
        this.originalSeed = state.originalSeed;
    }
    
    /**
     * Resets the generator back to its original seed
     */
    reset() {
        this.currentSeed = this.originalSeed;
    }
    
    /**
     * Gets the original seed value
     * 
     * @returns {number} The original seed used to initialize this generator
     */
    getOriginalSeed() {
        return this.originalSeed;
    }
    
    /**
     * Creates a new RandomState instance with the same seed
     * 
     * @returns {RandomState} New instance with identical seed
     */
    clone() {
        const cloned = new RandomState(this.originalSeed);
        cloned.currentSeed = this.currentSeed;
        return cloned;
    }
    
    /**
     * Global toggle state storage for togglingRandomBool
     */
    static _toggleStates = {};
    
    /**
     * When called the first time for `key`, returns a random bool, but thereafter returns the negated bool value each time.
     * If randomState is provided, the seed is tracked - calling again with the same seed returns the same value
     * (for print/preview consistency), while a different seed triggers a toggle (for remix).
     *
     * @param {string} key - Unique key to track toggle state
     * @param {RandomState} randomState - Optional RandomState instance for deterministic initial value
     * @returns {boolean} Toggle value for the key
     */
    static togglingRandomBool(key, randomState = null) {
        const state = RandomState._toggleStates[key];
        const currentSeed = randomState?.getOriginalSeed();

        if (state === undefined) {
            // First call - compute initial value
            const value = randomState ? randomState.randomBool() : Math.random() < 0.5;
            RandomState._toggleStates[key] = { value, seed: currentSeed };
            return value;
        }

        // If same seed as stored, return same value (print matching preview)
        if (currentSeed !== undefined && state.seed === currentSeed) {
            return state.value;
        }

        // Different seed or no seed - toggle (remix behavior)
        const newValue = !state.value;
        RandomState._toggleStates[key] = { value: newValue, seed: currentSeed };
        return newValue;
    }
}